
    import { animate, stagger, splitText } from 'animejs';
    import { initNavPill } from '../../src/lib/navPill.js';

    /* Size the scrolled nav island to its real content so the CTA
       can't hang outside the plate (see src/lib/navPill.js). */
    initNavPill(document.querySelector('.sv-nav'));


    /* Hero headline — per-char blur reveal, mirroring the home page
       hero (.about-hero__headline in src/new-animations.js). Suppress
       the CSS fallback animation, split into chars, prime each at
       opacity 0 / blur(12px), then tween to opacity 1 / blur(0).
       Reduced-motion users get the headline shown without tweens. */
    (function () {
      const headline = document.querySelector('.sv-hero__headline');
      if (!headline) return;

      const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

      // Kill the CSS fallback so it can't double-fire.
      headline.style.animation = 'none';
      headline.style.opacity = '0';
      headline.style.transform = 'none';

      const split = splitText(headline, { chars: { wrap: true, class: 'anime-split-char' }, accessible: true });
      const chars = split.chars;

      for (const c of chars) {
        c.style.opacity = '0';
        c.style.filter  = 'blur(12px)';
      }
      headline.style.opacity = '1';

      if (reduced) {
        for (const c of chars) { c.style.opacity = '1'; c.style.filter = 'none'; }
        return;
      }

      animate(chars, {
        opacity: [0, 1],
        filter: ['blur(12px)', 'blur(0px)'],
        duration: 1500,
        ease: 'outExpo',
        delay: stagger(28, { start: 180 }),
        onComplete: () => { for (const c of chars) c.style.willChange = 'auto'; },
      });
    })();

    /* Lead entrance — mirrors the home page services-section reveal:
       per-char blur+fade on the heading, scale+drop on the chip,
       translateY fade on the supporting copy. One IntersectionObserver
       triggers the lot when the lead is 15% in view. Reduced-motion
       users get the final state without tweens. */
    (function () {
      const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

      // Which leads get the reveal. The deliverables lead is the one
      // this started on and stays the behaviour for every page; the
      // compact scale takes the whole set, so a headline doesn't
      // arrive one way in one section and another way in the next.
      //
      // How-it-works is deliberately not here: it already runs the
      // scroll-driven magic-text reveal, and splitting it a second
      // time would have two drivers writing to the same characters.
      //
      // [root, heading, chip, sub] — each root is queried for its own
      // parts, so the two card sections (deliverables and toolkit)
      // reveal independently as each comes into view.
      const LEAD = ['.sv-deliv__lead', '.sv-deliv__heading', '.sv-deliv__chip', '.sv-deliv__sub'];
      const specs = document.querySelector('main.sv-compact')
        ? [
            LEAD,
            ['.sv-stat__inner', '.sv-stat__heading', '.sv-deliv__chip', '.sv-stat__body'],
            ['.sv-show__lead', '.sv-show__heading', '.sv-deliv__chip', '.sv-show__sub'],
            ['.sv-price__lead', '.sv-price__heading', '.sv-deliv__chip', '.sv-price__sub'],
            ['.sv-qualifier__lead', '.sv-qualifier__heading', '.sv-deliv__chip', '.sv-qualifier__sub'],
            ['.sv-faq__lead', '.sv-faq__heading', null, '.sv-faq__sub'],
            ['.sv-finalcta__inner', '.sv-finalcta__heading', null, '.sv-finalcta__sub'],
          ]
        : [LEAD];

      const groups = [];
      for (const [rootSel, headingSel, chipSel, subSel] of specs) {
        for (const root of document.querySelectorAll(rootSel)) {
          const heading = root.querySelector(headingSel);
          if (!heading) continue;
          groups.push({
            root,
            heading,
            chip: chipSel ? root.querySelector(chipSel) : null,
            sub: subSel ? root.querySelector(subSel) : null,
          });
        }
      }
      if (!groups.length) return;

      for (const g of groups) {
        const chars = splitText(g.heading, {
          chars: { wrap: true, class: 'anime-split-char' },
          accessible: true,
        }).chars;

        // Initial state — chars blurred + invisible, chip + sub offset.
        for (const c of chars) {
          c.style.opacity = '0';
          c.style.filter  = 'blur(12px)';
        }
        g.heading.style.opacity = '1';
        if (g.chip) { g.chip.style.opacity = '0'; g.chip.style.transform = 'translateY(-6px) scale(0.96)'; g.chip.style.willChange = 'opacity, transform'; }
        if (g.sub)  { g.sub.style.opacity  = '0'; g.sub.style.transform  = 'translateY(10px)'; g.sub.style.willChange = 'opacity, transform'; }

        if (reduced) {
          for (const c of chars) { c.style.opacity = '1'; c.style.filter = 'none'; }
          if (g.chip) { g.chip.style.opacity = '1'; g.chip.style.transform = 'none'; g.chip.style.willChange = ''; }
          if (g.sub)  { g.sub.style.opacity  = '1'; g.sub.style.transform  = 'none'; g.sub.style.willChange  = ''; }
          continue;
        }

        const io = new IntersectionObserver((entries, obs) => {
          for (const e of entries) {
            if (!e.isIntersecting) continue;

            if (g.chip) {
              animate(g.chip, {
                opacity: [0, 1],
                translateY: [-6, 0],
                scale: [0.96, 1],
                duration: 600,
                ease: 'outExpo',
                onComplete: () => { g.chip.style.willChange = ''; },
              });
            }

            animate(chars, {
              opacity: [0, 1],
              filter: ['blur(12px)', 'blur(0px)'],
              duration: 1500,
              ease: 'outExpo',
              delay: stagger(28, { start: 240 }),
              onComplete: () => {
                for (const c of chars) c.style.willChange = 'auto';
              },
            });

            if (g.sub) {
              animate(g.sub, {
                opacity: [0, 1],
                translateY: [10, 0],
                duration: 1100,
                ease: 'outExpo',
                delay: 520,
                onComplete: () => { g.sub.style.willChange = ''; },
              });
            }

            obs.disconnect();
          }
        }, { threshold: 0.15 });
        io.observe(g.root);
      }
    })();

    const tabsRoot   = document.querySelector('[data-deliv-tabs]');
    const panelsRoot = document.querySelector('[data-deliv-panels]');
    const selectEl   = document.querySelector('[data-deliv-select]');
    if (tabsRoot && panelsRoot) {
      const tabs    = tabsRoot.querySelectorAll('[data-deliv-tab]');
      const panels  = panelsRoot.querySelectorAll('[data-deliv-panel]');
      const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

      function activate (idx) {
        tabs.forEach((t) => {
          const active = +t.dataset.delivTab === idx;
          t.classList.toggle('is-active', active);
          t.setAttribute('aria-selected', active ? 'true' : 'false');
        });
        panels.forEach((p) => {
          const active = +p.dataset.delivPanel === idx;
          p.classList.toggle('is-active', active);
          if (active && !reduced) {
            animate(p, {
              opacity: [0, 1],
              translateY: [12, 0],
              duration: 460,
              easing: 'easeOutCubic',
            });
          }
        });
        if (selectEl && +selectEl.value !== idx) selectEl.value = String(idx);
      }

      tabs.forEach((tab) => {
        tab.addEventListener('click', () => activate(+tab.dataset.delivTab));
      });

      // Mobile <select> mirrors the tabs.
      if (selectEl) {
        selectEl.addEventListener('change', () => activate(+selectEl.value));
      }
    }

    // FAQ accordion — lean click toggle, CSS drives the open/close
    // (grid-template-rows 0fr → 1fr on .sv-faq__a).
    document.querySelectorAll('[data-faq-item]').forEach((item) => {
      const btn = item.querySelector('.sv-faq__q');
      const answer = item.querySelector('.sv-faq__a');
      if (!btn || !answer) return;
      btn.addEventListener('click', () => {
        const isOpen = item.classList.toggle('is-open');
        btn.setAttribute('aria-expanded', String(isOpen));
        answer.setAttribute('aria-hidden', String(!isOpen));
      });
    });
  